<template>
  <div class="contacts-table-container">

    <!-- ========== TABLA DE CONTACTOS ========== -->
    <table class="contacts-table">
      <thead>
        <tr>
          <th>Cliente</th>
          <th>Teléfono</th>
          <th>Tags</th>
          <th>Escalado</th>
          <th>Acciones</th>
        </tr>
      </thead>

      <tbody>
        <!-- Estado: Cargando -->
        <tr v-if="loading" class="state-row">
          <td colspan="5">
            <div class="state-box loading" aria-live="polite" aria-busy="true">
              <span class="spinner"></span>
              <div class="state-text">
                <strong>Cargando contactos…</strong>
                <small>Por favor espera un momento.</small>
              </div>
            </div>
          </td>
        </tr>

        <!-- Estado: Sin datos -->
        <tr v-else-if="contacts.length === 0" class="state-row">
          <td colspan="5">
            <div class="state-box empty" aria-live="polite">
              <ion-icon name="people-outline" class="state-icon"></ion-icon>
              <div class="state-text">
                <strong>Sin resultados</strong>
                <small>No encontramos contactos con los filtros actuales.</small>
              </div>
            </div>
          </td>
        </tr>

        <!-- ========== FILAS DE CONTACTOS ========== -->
        <tr v-else v-for="c in contacts" :key="c.id">
          <td class="cell-name">{{ c.name }}</td>
          <td>{{ c.phone_number }}</td>

          <td>
            <div class="tags">
              <span
                v-for="(t, idx) in c.tags"
                :key="idx"
                class="tag-chip"
                :style="t.color ? { borderColor: t.color, color: t.color } : {}"
              >
                <span class="swatch" :style="{ background: t.color }"></span>
                {{ t.name }}
              </span>
              <span v-if="!c.tags || c.tags.length === 0" class="tag-empty">—</span>
            </div>
          </td>

          <td>
            <span
              :class="['status-badge', c.is_escalated_n8n ? 'active' : 'inactive']"
            >
              {{ c.is_escalated_n8n ? 'Escalado' : 'No Escalado' }}
            </span>
          </td>

          <td class="cell-actions">
            <button class="icon-btn chat" @click="$emit('chat', c)">
              <ion-icon name="chatbubble-ellipses-outline" />
            </button>

            <button class="icon-btn tag" @click="$emit('manage-tags', c)" title="Gestionar tags">
              <ion-icon name="pricetags-outline" />
            </button>

            <button
              class="icon-btn record"
              :class="{ active: c.has_schedule_record }"
              :disabled="c.__toggling"
              @click="$emit('toggle-record', c)"
              :title="c.__toggling ? 'Procesando…' : (c.has_schedule_record ? 'Quitar registro' : 'Asignar registro')"
            >
              <ion-icon
                v-if="c.__toggling"
                :key="`spin-${c.id}`"
                class="spin"
                name="reload-outline"
              />
              <ion-icon
                v-else
                :key="`icon-${c.id}-${c.has_schedule_record ? 'on' : 'off'}`"
                :name="c.has_schedule_record ? 'document-attach-outline' : 'document-text-outline'"
              />
            </button>

            <button class="icon-btn delete" @click="$emit('delete', c)">
              <ion-icon name="trash-outline" />
            </button>
          </td>
        </tr>
      </tbody>
    </table>

  </div>
</template>

<script>
export default {
  name: "ContactsTable",

  props: {
    contacts: {
      type: Array,
      required: true,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  emits: [
    "chat",
    "delete",
    "toggle-record",
  ]
};
</script>

<style scoped>
/* ========== CONTENEDOR ========== */
.contacts-table-container {
  overflow-x: auto;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  background-color: #fff;
  width: 95%;
  margin: 1.5rem auto;
  box-shadow: 0 4px 12px rgba(0,0,0,0.04);
}

/* ========== TABLA ========== */
.contacts-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Manrope', sans-serif;
}

.contacts-table th {
  text-align: left;
  padding: 0.85rem 1rem;
  font-size: 0.85rem;
  font-weight: 700;
  color: #475569;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.contacts-table td {
  text-align: left;
  padding: 0.9rem 1rem;
  font-size: 0.95rem;
  color: #0f172a;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.contacts-table th:nth-child(3),
.contacts-table td:nth-child(3),
.contacts-table th:nth-child(4),
.contacts-table td:nth-child(4),
.contacts-table th:nth-child(5),
.contacts-table td:nth-child(5) {
  text-align: center;
}

.contacts-table th:nth-child(1),
.contacts-table td:nth-child(1) {
  width: 25%;   
}

.contacts-table th:nth-child(3),
.contacts-table td:nth-child(3) {
  width: 20%; 
}

/* ========== CELDA DE NOMBRE ========== */
.cell-name {
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px; 
}

/* ========== TAGS ========== */
.tags {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.4rem;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.6rem;
  font-size: 0.8rem;
  font-weight: 600;
  border-radius: 9999px;
  border: 1.5px solid #cbd5e1;
  background-color: #f8fafc;
  transition: all 0.25s ease;
}

.tag-chip:hover {
  background-color: #f1f5f9;
  transform: translateY(-1px);
}

.swatch {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.tag-empty {
  font-size: 0.8rem;
  color: #94a3b8;
  font-style: italic;
}

/* ========== BADGE ESCALADO ========== */
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.8rem;
  font-size: 0.8rem;
  font-weight: 700;
  border-radius: 9999px;
  transition: background-color 0.25s ease, color 0.25s ease, transform 0.2s ease;
}

.status-badge.active {
  background-color: rgba(220, 38, 38, 0.08);
  color: #dc2626;
  border: 1px solid rgba(220, 38, 38, 0.2);
}

.status-badge.inactive {
  background-color: rgba(34, 197, 94, 0.08);
  color: #16a34a;
  border: 1px solid rgba(34, 197, 94, 0.2);
}

/* ========== ACCIONES ========== */
.cell-actions {
  display: flex;
  gap: 0.6rem;
  justify-content: flex-start;
  align-items: center;
  justify-content: center;
}

.icon-btn {
  all: unset;
  cursor: pointer;
  font-size: 1.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem;
  border-radius: 8px;
  transition: all 0.25s ease;
}

/* Chat */
.icon-btn.chat {
  color: #2563eb;
  border: 1.5px solid rgba(37, 99, 235, 0.25);
  background-color: rgba(37, 99, 235, 0.05);
}
.icon-btn.chat:hover {
  background-color: rgba(37, 99, 235, 0.15);
  transform: translateY(-1px) scale(1.05);
}

/* Tags */
.icon-btn.tag {
  color: #16a34a;
  border: 1.5px solid rgba(22, 163, 74, 0.25);
  background-color: rgba(22, 163, 74, 0.05);
}

.icon-btn.tag:hover {
  background-color: rgba(22, 163, 74, 0.15);
  transform: translateY(-1px) scale(1.05);
}

/* Registro */
.icon-btn.record {
  color: #0ea5e9; 
  border: 1.5px solid rgba(14, 165, 233, 0.25);
  background-color: rgba(14, 165, 233, 0.05);
  transition: all 0.25s ease;
}

.icon-btn.record:hover {
  background-color: rgba(14, 165, 233, 0.12);
  transform: translateY(-1px) scale(1.05);
}

.icon-btn.record.active {
  color: #0ea5e9;          
  background-color: #e0f2fe;
  border-color: #bae6fd;    
  box-shadow: inset 0 0 0 1.5px #0ea5e9;
}

/* Eliminar */
.icon-btn.delete {
  color: #dc2626;
  border: 1.5px solid rgba(220, 38, 38, 0.2);
  background-color: rgba(220, 38, 38, 0.05);
}
.icon-btn.delete:hover {
  background-color: rgba(220, 38, 38, 0.15);
  transform: translateY(-1px) scale(1.05);
}

/* ========== MENSAJES ========== */
.state-row td {
  padding: 1rem 1rem;
  background: #f8fafc;
}

.state-box {
  max-width: 560px;
  margin: 0 auto;
  padding: 1rem 1.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.85rem;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.85rem;
  box-shadow: 0 6px 18px rgba(2, 6, 23, 0.04);
}

.state-box.loading {
  background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
}

.state-box.empty {
  background: #ffffff;
}

.state-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.state-text strong {
  font-size: 0.98rem;
  color: #0f172a;
  font-weight: 800;
  letter-spacing: .1px;
}

.state-text small {
  font-size: 0.88rem;
  color: #64748b;
}

.state-icon {
  font-size: 1.35rem;
  color: #94a3b8;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2.5px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .spinner { animation: none; border-top-color: #3b82f6; }
}

@keyframes spin { to { transform: rotate(360deg); } }
.icon-btn .spin { animation: spin .9s linear infinite; }

.icon-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

</style>